import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  ElementRef,
  SimpleChanges,
  OnChanges,
  ChangeDetectionStrategy,
} from "@angular/core";
import { select } from "d3-selection";
import { id } from "@swimlane/ngx-charts/release/utils";

@Component({
  selector: "g[iris-box-plot-element]",
  templateUrl: "./iris-chart-box-plot-element.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisBoxPlotElementComponent implements OnChanges {
  @Input() fill;
  @Input() data;
  @Input() width;
  @Input() height;
  @Input() x;
  @Input() y;
  @Input() yW;
  @Input() yA;
  @Input() yM;
  @Input() heightW;
  @Input() orientation;
  @Input() roundEdges: boolean = true;
  @Input() gradient: boolean = false;
  @Input() offset = 0;
  @Input() isActive: boolean = false;
  @Input() stops: any[];
  @Input() animations: boolean = true;

  @Output() select = new EventEmitter();
  @Output() activate = new EventEmitter();
  @Output() deactivate = new EventEmitter();

  element: any;
  path: any;
  pathW: any;
  pathM: any;
  gradientId: any;
  gradientFill: any;
  startOpacity: any;
  initialized: boolean = false;
  gradientStops: any[];
  hasGradient: boolean = false;

  constructor(element: ElementRef) {
    this.element = element.nativeElement;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) {
      this.loadAnimation();
      this.initialized = true;
    } else {
      this.update();
    }
  }

  update(): void {
    this.gradientId = "grad" + id().toString();
    this.gradientFill = `url(#${this.gradientId})`;

    if (this.gradient || this.stops) {
      this.gradientStops = this.getGradient();
      this.hasGradient = true;
    } else {
      this.hasGradient = false;
    }

    this.updatePathEl();
  }

  loadAnimation(): void {
    this.path = this.getStartingPath();
    this.pathW = this.getStartingPathW();
    this.pathM = this.getStartingPathM();
    setTimeout(this.update.bind(this), 100);
  }

  updatePathEl(): void {
    const node = select(this.element).select("#bar");
    const whiskers = select(this.element).select("#whiskers");
    const markers = select(this.element).select("#markers");
    const path = this.getPath();
    const pathW = this.getPathW();
    const pathM = this.getPathM();
    if (this.animations) {
      node.transition().duration(500).attr("d", path);
      whiskers.transition().duration(500).attr("d", pathW);
      markers.transition().duration(500).attr("d", pathM);
    } else {
      node.attr("d", path);
      whiskers.attr("d", pathW);
      markers.attr("d", pathM);
    }
  }

  getGradient() {
    if (this.stops) {
      return this.stops;
    }

    return [
      {
        offset: 0,
        color: this.fill,
        opacity: this.getStartOpacity(),
      },
      {
        offset: 100,
        color: this.fill,
        opacity: 1,
      },
    ];
  }

  getStartingPath() {
    if (!this.animations) {
      return this.getPath();
    }

    let radius = this.getRadius();
    let path;

    if (this.roundEdges) {
      if (this.orientation === "vertical") {
        radius = Math.min(this.height, radius);
        path = this.roundedRect(
          this.x,
          this.y + this.height,
          this.width,
          1,
          0,
          this.edges
        );
      } else if (this.orientation === "horizontal") {
        radius = Math.min(this.width, radius);
        path = this.roundedRect(this.x, this.y, 1, this.height, 0, this.edges);
      }
    } else {
      if (this.orientation === "vertical") {
        path = this.roundedRect(
          this.x,
          this.y + this.height,
          this.width,
          1,
          0,
          this.edges
        );
      } else if (this.orientation === "horizontal") {
        path = this.roundedRect(this.x, this.y, 1, this.height, 0, this.edges);
      }
    }

    return path;
  }

  getStartingPathW() {
    let path = `M${[this.x + this.width / 2, this.yW + this.heightW]}`;
    path += `L${[this.x + this.width / 2, this.yW + this.heightW]}`;

    path += `M${[this.x + this.width / 2, this.yW + this.heightW]}`;
    path += `L${[this.x + this.width / 2, this.yW + this.heightW]}`;

    path += `M${[this.x + this.width / 2, this.yW + this.heightW]}`;
    path += `L${[this.x + this.width / 2, this.yW + this.heightW]}`;

    path += `M${[this.x + this.width / 2, this.yW + this.heightW]}`;
    path += `L${[this.x + this.width / 2, this.yW + this.heightW]}`;

    return path;
  }

  getStartingPathM() {
    let path = `M${[this.x + this.width / 2, this.y + this.height]}`;
    path += `L${[this.x + this.width / 2, this.y + this.height]}`;

    path += `M${[this.x + this.width / 2, this.y + this.height]}`;
    path += `L${[this.x + this.width / 2, this.y + this.height]}`;

    path += `M${[this.x + this.width / 2, this.y + this.height]}`;
    path += `L${[this.x + this.width / 2, this.y + this.height]}`;

    return path;
  }

  getPath() {
    let radius = this.getRadius();
    let path;

    if (this.roundEdges) {
      if (this.orientation === "vertical") {
        radius = Math.min(this.height, radius);
        path = this.roundedRect(
          this.x,
          this.y,
          this.width,
          this.height,
          radius,
          this.edges
        );
      } else if (this.orientation === "horizontal") {
        radius = Math.min(this.width, radius);
        path = this.roundedRect(
          this.x,
          this.y,
          this.width,
          this.height,
          radius,
          this.edges
        );
      }
    } else {
      path = this.roundedRect(
        this.x,
        this.y,
        this.width,
        this.height,
        radius,
        this.edges
      );
    }

    return path;
  }

  getPathW() {
    let path = `M${[this.x + this.width / 2, this.yW + this.heightW]}`;
    path += `L${[this.x + this.width / 2, this.y + this.height]}`;

    path += `M${[this.x + this.width / 2, this.yW]}`;
    path += `L${[this.x + this.width / 2, this.y]}`;

    path += `M${[
      this.x + this.width / 2 - this.width / 6,
      this.yW + this.heightW,
    ]}`;
    path += `L${[
      this.x + this.width / 2 + this.width / 6,
      this.yW + this.heightW,
    ]}`;

    path += `M${[this.x + this.width / 2 - this.width / 6, this.yW]}`;
    path += `L${[this.x + this.width / 2 + this.width / 6, this.yW]}`;

    return path;
  }

  getPathM() {
    const crossSize = 5;

    let path = `M${[this.x, this.yM]}`;
    path += `L${[this.x + this.width, this.yM]}`;

    path += `M${[this.x + this.width / 2 - crossSize, this.yA - crossSize]}`;
    path += `L${[this.x + this.width / 2 + crossSize, this.yA + crossSize]}`;

    path += `M${[this.x + this.width / 2 - crossSize, this.yA + crossSize]}`;
    path += `L${[this.x + this.width / 2 + crossSize, this.yA - crossSize]}`;

    return path;
  }

  getRadius(): number {
    let radius = 0;

    if (this.roundEdges && this.height > 5 && this.width > 5) {
      radius = Math.floor(Math.min(5, this.height / 2, this.width / 2));
    }

    return radius;
  }

  getStartOpacity(): number {
    if (this.roundEdges) {
      return 0.2;
    } else {
      return 0.5;
    }
  }

  get edges() {
    let edges = [false, false, false, false];
    if (this.roundEdges) {
      if (this.orientation === "vertical") {
        if (this.data.value > 0) {
          edges = [true, true, false, false];
        } else {
          edges = [false, false, true, true];
        }
      } else if (this.orientation === "horizontal") {
        if (this.data.value > 0) {
          edges = [false, true, false, true];
        } else {
          edges = [true, false, true, false];
        }
      }
    }
    return edges;
  }

  roundedRect(x, y, w, h, r, [tl, tr, bl, br]: boolean[]) {
    let retval = "";

    w = Math.floor(w);
    h = Math.floor(h);

    w = w === 0 ? 1 : w;
    h = h === 0 ? 1 : h;

    retval = `M${[x + r, y]}`;
    retval += `h${w - 2 * r}`;

    if (tr) {
      retval += `a${[r, r]} 0 0 1 ${[r, r]}`;
    } else {
      retval += `h${r}v${r}`;
    }

    retval += `v${h - 2 * r}`;

    if (br) {
      retval += `a${[r, r]} 0 0 1 ${[-r, r]}`;
    } else {
      retval += `v${r}h${-r}`;
    }

    retval += `h${2 * r - w}`;

    if (bl) {
      retval += `a${[r, r]} 0 0 1 ${[-r, -r]}`;
    } else {
      retval += `h${-r}v${-r}`;
    }

    retval += `v${2 * r - h}`;

    if (tl) {
      retval += `a${[r, r]} 0 0 1 ${[r, -r]}`;
    } else {
      retval += `v${-r}h${r}`;
    }

    retval += `z`;

    return retval;
  }

  @HostListener("mouseenter")
  onMouseEnter(): void {
    this.activate.emit(this.data);
  }

  @HostListener("mouseleave")
  onMouseLeave(): void {
    this.deactivate.emit(this.data);
  }
}



// WEBPACK FOOTER //
// /agir/src/app/components/iris-charts-box-plot-chart/iris-chart-box-plot-element.component.ts